/**
 * Schema Exploration Panel - Phase 1 of Knowledge Graph Design
 *
 * Allows users to:
 * - Browse enterprise system modules and tables
 * - Get AI-powered table suggestions based on strategic questions
 * - Get AI Digital Twin recommendations for comprehensive table selection
 * - Select/deselect tables for the case schema
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Table2,
  Columns3,
  Search,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  Loader2,
  AlertCircle,
  Info,
  RefreshCw,
  Filter,
  Brain,
  Star,
  TrendingUp,
  GitBranch,
  Clock,
  CheckCircle2,
  Eye,
} from 'lucide-react';
import {
  getModulesForSystem,
  getColumnsForTable,
  suggestTablesForQuestions,
  selectTablesForCase,
  deselectTablesFromCase,
  getDigitalTwinRecommendations,
  getKGFirstConceptRecommendations,
  getSavedKGFirstConceptRecommendations,
  saveKGFirstConceptRecommendations,
  getSelectedTablesForCase,
  type SelectedTableInfo,
  type KGFirstConceptRecommendationsResponse,
} from '../../lib/api/knowledgeGraphDesignApi';
import type {
  ModuleWithTables,
  SchemaTable,
  TableColumn,
  TableSuggestion,
  DigitalTwinTableRecommendation,
  RecommendedTable,
} from '../../types/knowledgeGraphDesign';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';
import { RecommendedTableCard } from './RecommendedTableCard';
import { toast } from 'sonner';

interface SchemaExplorationPanelProps {
  caseId: string;
  systemIds: string[];
  systemNames: string[];
  questionIds?: string[];
  onTablesSelected?: (tableIds: string[]) => void;
}

export const SchemaExplorationPanel: React.FC<SchemaExplorationPanelProps> = ({
  caseId,
  systemIds,
  systemNames,
  questionIds,
  onTablesSelected,
}) => {
  // State for currently selected system in the tab view
  const [activeSystemIndex, setActiveSystemIndex] = useState(0);
  const activeSystemId = systemIds[activeSystemIndex];
  const activeSystemName = systemNames[activeSystemIndex];
  const queryClient = useQueryClient();

  // UI State
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);
  const [viewMode, setViewMode] = useState<'browse' | 'suggestions' | 'digital-twin'>('browse');
  const [dtRecommendationCategory, setDtRecommendationCategory] = useState<'must_have' | 'should_have' | 'could_have'>('must_have');

  // Fetch modules and tables for active system
  const {
    data: modules,
    isLoading: loadingModules,
    error: modulesError,
    refetch: refetchModules,
  } = useQuery({
    queryKey: ['schema-exploration', 'modules', caseId, activeSystemId],
    queryFn: () => getModulesForSystem(caseId, activeSystemId),
    enabled: !!activeSystemId,
  });

  // Fetch AI suggestions for active system
  const {
    data: suggestionsData,
    isLoading: loadingSuggestions,
    refetch: refetchSuggestions,
  } = useQuery({
    queryKey: ['schema-exploration', 'suggestions', caseId, activeSystemId, questionIds],
    queryFn: () => suggestTablesForQuestions(caseId, activeSystemId, questionIds, 30),
    enabled: showSuggestionsPanel && !!activeSystemId,
  });

  // Digital Twin / KG-First recommendation state
  const [dtRecommendation, setDtRecommendation] = useState<DigitalTwinTableRecommendation | null>(null);
  const [kgFirstRecommendation, setKgFirstRecommendation] = useState<KGFirstConceptRecommendationsResponse | null>(null);
  const [isLoadingDtRecommendation, setIsLoadingDtRecommendation] = useState(false);
  const [dtRecommendationError, setDtRecommendationError] = useState<string | null>(null);
  // Use KG-First approach (concept-based) instead of table-based recommendations
  const [useKGFirstMode, setUseKGFirstMode] = useState(true);

  // Existing selected tables state (from Neo4j)
  const [existingTables, setExistingTables] = useState<SelectedTableInfo[]>([]);
  const [isLoadingExistingTables, setIsLoadingExistingTables] = useState(true);
  const [showExistingTablesView, setShowExistingTablesView] = useState(false);

  // Check for existing selected tables on mount
  useEffect(() => {
    const checkExistingTables = async () => {
      setIsLoadingExistingTables(true);
      try {
        const tables = await getSelectedTablesForCase(caseId);
        setExistingTables(tables);
        // Pre-select existing tables in the selection state
        if (tables.length > 0) {
          const existingIds = new Set(tables.map(t => t.table_id));
          setSelectedTableIds(existingIds);
          // Notify parent about existing tables so Next button gets enabled
          onTablesSelected?.(Array.from(existingIds));
        }
      } catch (error) {
        console.error('Failed to fetch existing tables:', error);
        // Not an error - just means no tables selected yet
      } finally {
        setIsLoadingExistingTables(false);
      }
    };
    checkExistingTables();
  }, [caseId, onTablesSelected]);

  // Check for saved KG-First concept recommendations on mount.
  //
  // `onTablesSelected` is stashed in a ref instead of being a dep because
  // parents typically pass a non-memoized inline callback — keeping it in
  // the dep array re-fires the effect every parent render, which previously
  // thrashed the network with repeat GETs to the saved-recommendations
  // endpoint and could feedback-loop via the onTablesSelected call inside.
  const onTablesSelectedRef = useRef(onTablesSelected);
  useEffect(() => {
    onTablesSelectedRef.current = onTablesSelected;
  }, [onTablesSelected]);

  useEffect(() => {
    const loadSavedRecommendations = async () => {
      if (!useKGFirstMode) return;

      try {
        console.log('📋 Checking for saved KG-First recommendations...');
        const savedRecs = await getSavedKGFirstConceptRecommendations(caseId);
        if (savedRecs) {
          console.log('✅ Found saved recommendations:', savedRecs.summary);
          setKgFirstRecommendation(savedRecs);
          setViewMode('digital-twin');
          // Enable Next button since we have recommendations
          onTablesSelectedRef.current?.([`kg-first-${savedRecs.summary.total_concepts}-concepts`]);
        }
      } catch (error) {
        console.error('Failed to fetch saved KG-First recommendations:', error);
        // Not an error - just means no recommendations saved yet
      }
    };
    loadSavedRecommendations();
  }, [caseId, useKGFirstMode]);

  // Fetch Digital Twin / KG-First recommendations
  const fetchDigitalTwinRecommendations = useCallback(async () => {
    setIsLoadingDtRecommendation(true);
    setDtRecommendationError(null);
    try {
      if (useKGFirstMode) {
        // Use KG-First concept-based recommendations (no Neo4j tables required)
        console.log('🧠 Using KG-First concept recommendations...');
        const kgResult = await getKGFirstConceptRecommendations(caseId);
        setKgFirstRecommendation(kgResult);
        setDtRecommendation(null); // Clear old table-based recommendations

        if (kgResult.error) {
          setDtRecommendationError(kgResult.error);
        } else {
          // Auto-save KG-First recommendations to Neo4j for persistence
          console.log('💾 Saving KG-First recommendations to Neo4j...');
          try {
            const saveResult = await saveKGFirstConceptRecommendations(caseId, kgResult);
            console.log('✅ Saved recommendations:', saveResult);
            toast.success(`${saveResult.concepts_saved} concepts saved`);
            // Enable Next button by notifying parent
            onTablesSelected?.([`kg-first-${kgResult.summary.total_concepts}-concepts`]);
          } catch (saveError) {
            console.error('Failed to save KG-First recommendations:', saveError);
            // Don't fail the whole operation - recommendations are still displayed
            toast.warning('Recommendations generated but failed to save for next visit');
          }
        }
      } else {
        // Fallback: Use table-based digital twin recommendations (requires Neo4j tables)
        console.log('📊 Using table-based digital twin recommendations...');
        const result = await getDigitalTwinRecommendations(caseId, {
          system_ids: systemIds,
          include_cross_system: true,
          include_foundation: true,
          include_could_have: true,
          max_tables_per_category: 50,
        });
        setDtRecommendation(result);
        setKgFirstRecommendation(null); // Clear KG-First recommendations
      }
    } catch (error: any) {
      console.error('Failed to fetch recommendations:', error);
      setDtRecommendationError(error.message || 'Failed to generate recommendations');
    } finally {
      setIsLoadingDtRecommendation(false);
    }
  }, [caseId, systemIds, useKGFirstMode, onTablesSelected]);

  // Column details cache
  const [columnCache, setColumnCache] = useState<Record<string, TableColumn[]>>({});
  const [loadingColumns, setLoadingColumns] = useState<Set<string>>(new Set());

  // Select tables mutation
  const selectTablesMutation = useMutation({
    mutationFn: (tableIds: string[]) => selectTablesForCase(caseId, activeSystemId, tableIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['schema-exploration'] });
      onTablesSelected?.(Array.from(selectedTableIds));
      const requested = data.tables_requested || variables.length;
      const saved = data.tables_selected || 0;
      const notFound = data.not_found_table_ids || [];

      console.log('📌 Save result:', {
        requested,
        saved,
        notFoundCount: notFound.length,
        notFoundIds: notFound.slice(0, 5),
        savedTables: data.saved_tables?.map((t: any) => t.table_name)
      });

      if (saved === requested) {
        toast.success(`${saved} tables saved to Neo4j successfully`);
      } else if (saved > 0) {
        toast.warning(
          `${saved} of ${requested} tables saved. ${notFound.length} tables have invalid IDs (not found in Neo4j).`,
          { duration: 6000 }
        );
      } else {
        toast.error(`Failed to save tables. ${notFound.length} table IDs not found in Neo4j.`);
      }
    },
    onError: (error: any) => {
      console.error('Failed to save tables:', error);
      toast.error(`Failed to save tables: ${error.message || 'Unknown error'}`);
    },
  });

  // Deselect tables mutation
  const deselectTablesMutation = useMutation({
    mutationFn: (tableIds: string[]) => deselectTablesFromCase(caseId, activeSystemId, tableIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['schema-exploration'] });
      toast.success(`${data.tables_deselected || 'Tables'} removed from selection`);
    },
    onError: (error: any) => {
      console.error('Failed to deselect tables:', error);
      toast.error(`Failed to remove tables: ${error.message || 'Unknown error'}`);
    },
  });

  // Toggle module expansion
  const toggleModule = useCallback((moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  // Toggle table expansion and load columns
  const toggleTable = useCallback(
    async (tableId: string) => {
      const isExpanding = !expandedTables.has(tableId);

      setExpandedTables((prev) => {
        const next = new Set(prev);
        if (next.has(tableId)) {
          next.delete(tableId);
        } else {
          next.add(tableId);
        }
        return next;
      });

      // Load columns if expanding and not cached
      if (isExpanding && !columnCache[tableId]) {
        setLoadingColumns((prev) => new Set(prev).add(tableId));
        try {
          const columns = await getColumnsForTable(caseId, activeSystemId, tableId);
          setColumnCache((prev) => ({ ...prev, [tableId]: columns }));
        } catch (error) {
          console.error('Failed to load columns:', error);
        } finally {
          setLoadingColumns((prev) => {
            const next = new Set(prev);
            next.delete(tableId);
            return next;
          });
        }
      }
    },
    [caseId, activeSystemId, expandedTables, columnCache]
  );

  // Toggle table selection
  const toggleTableSelection = useCallback((tableId: string) => {
    setSelectedTableIds((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) {
        next.delete(tableId);
      } else {
        next.add(tableId);
      }
      return next;
    });
  }, []);

  // Select all suggestions
  const selectAllSuggestions = useCallback(() => {
    if (suggestionsData?.suggestions) {
      const newIds = new Set(selectedTableIds);
      suggestionsData.suggestions.forEach((s) => newIds.add(s.table_id));
      setSelectedTableIds(newIds);
    }
  }, [suggestionsData, selectedTableIds]);

  // Select all tables in a Digital Twin category
  const selectAllInDtCategory = useCallback((category: 'must_have' | 'should_have' | 'could_have') => {
    if (!dtRecommendation) return;

    const tables = category === 'must_have'
      ? dtRecommendation.must_have_tables
      : category === 'should_have'
      ? dtRecommendation.should_have_tables
      : dtRecommendation.could_have_tables;

    const newIds = new Set(selectedTableIds);
    tables.forEach((t) => newIds.add(t.table_id));
    setSelectedTableIds(newIds);
  }, [dtRecommendation, selectedTableIds]);

  // Select all Must-Have tables
  const selectAllMustHave = useCallback(() => {
    selectAllInDtCategory('must_have');
  }, [selectAllInDtCategory]);

  // Filter modules and tables by search query
  const filteredModules = useMemo(() => {
    if (!modules) return [];
    if (!searchQuery.trim()) return modules;

    const query = searchQuery.toLowerCase();
    return modules
      .map((module) => ({
        ...module,
        tables: (module.tables ?? []).filter(
          (table) =>
            table.table_name.toLowerCase().includes(query) ||
            table.description?.toLowerCase().includes(query)
        ),
      }))
      .filter(
        (module) =>
          module.module_name.toLowerCase().includes(query) ||
          module.description?.toLowerCase().includes(query) ||
          module.tables.length > 0
      );
  }, [modules, searchQuery]);

  // Handle save selection
  const handleSaveSelection = useCallback(() => {
    const tableIds = Array.from(selectedTableIds);
    console.log(`📌 Saving ${tableIds.length} tables to Neo4j:`, tableIds);
    if (tableIds.length > 0) {
      selectTablesMutation.mutate(tableIds);
    }
  }, [selectedTableIds, selectTablesMutation]);

  // Render column list
  const renderColumns = (tableId: string) => {
    const columns = columnCache[tableId];
    const isLoading = loadingColumns.has(tableId);

    if (isLoading) {
      return (
        <div className="flex items-center gap-2 py-2 px-4 text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading columns...</span>
        </div>
      );
    }

    if (!columns || columns.length === 0) {
      return (
        <div className="py-2 px-4 text-neutral-500 text-sm">No columns found</div>
      );
    }

    return (
      <div className="bg-neutral-50 border-t border-neutral-200">
        <div className="grid grid-cols-4 gap-2 px-4 py-2 text-xs font-medium text-neutral-500 border-b border-neutral-100">
          <span>Column Name</span>
          <span>Data Type</span>
          <span>Keys</span>
          <span>Description</span>
        </div>
        <div className="max-h-60 overflow-y-auto">
          {columns.map((column) => (
            <div
              key={column.column_id}
              className="grid grid-cols-4 gap-2 px-4 py-1.5 text-sm hover:bg-neutral-100 border-b border-neutral-50"
            >
              <span className="font-mono text-xs">{column.column_name}</span>
              <span className="text-neutral-600">{column.data_type}</span>
              <span className="flex gap-1">
                {column.is_primary_key && (
                  <Badge variant="default" className="text-xs px-1">PK</Badge>
                )}
                {column.is_foreign_key && (
                  <Badge variant="outline" className="text-xs px-1">FK</Badge>
                )}
              </span>
              <span className="text-neutral-500 text-xs truncate">
                {column.business_meaning || column.description || '-'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render table row
  const renderTable = (table: SchemaTable, moduleName: string) => {
    const isExpanded = expandedTables.has(table.table_id);
    const isSelected = selectedTableIds.has(table.table_id);

    return (
      <div key={table.table_id} className="border-b border-neutral-100 last:border-b-0">
        <div
          className={`flex items-center gap-3 px-4 py-2 hover:bg-neutral-50 cursor-pointer ${
            isSelected ? 'bg-primary-50' : ''
          }`}
        >
          {/* Selection checkbox */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleTableSelection(table.table_id);
            }}
            className={`w-5 h-5 rounded border flex items-center justify-center ${
              isSelected
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-neutral-300 hover:border-primary-400'
            }`}
          >
            {isSelected && <Check className="w-3 h-3" />}
          </button>

          {/* Table info */}
          <div
            className="flex-1 flex items-center gap-2"
            onClick={() => toggleTable(table.table_id)}
          >
            <button className="p-0.5 hover:bg-neutral-200 rounded">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-neutral-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              )}
            </button>
            <Table2 className="w-4 h-4 text-neutral-400" />
            <span className="font-medium text-sm">{table.table_name}</span>
            <Badge variant="outline" className="text-xs">
              {table.column_count ?? 0} cols
            </Badge>
          </div>

          {/* Primary keys indicator */}
          {(table.primary_keys?.length ?? 0) > 0 && (
            <span className="text-xs text-neutral-500">
              PK: {table.primary_keys?.join(', ')}
            </span>
          )}
        </div>

        {/* Expanded columns */}
        {isExpanded && renderColumns(table.table_id)}
      </div>
    );
  };

  // Render suggestion card
  const renderSuggestion = (suggestion: TableSuggestion) => {
    const isSelected = selectedTableIds.has(suggestion.table_id);

    return (
      <Card
        key={suggestion.table_id}
        className={`p-4 cursor-pointer transition-all ${
          isSelected ? 'ring-2 ring-primary-500 bg-primary-50' : 'hover:shadow-md'
        }`}
        onClick={() => toggleTableSelection(suggestion.table_id)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Table2 className="w-4 h-4 text-primary-600" />
              <span className="font-medium">{suggestion.table_name}</span>
              <Badge variant="outline" className="text-xs">
                {suggestion.module_name}
              </Badge>
            </div>
            <p className="text-sm text-neutral-600 mt-1">{suggestion.reason}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {(suggestion.data_categories ?? []).map((cat) => (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {cat}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                isSelected ? 'bg-primary-600 text-white' : 'bg-neutral-200'
              }`}
            >
              {isSelected && <Check className="w-4 h-4" />}
            </div>
            <span className="text-xs font-medium text-primary-600">
              {Math.round(suggestion.relevance_score * 100)}% match
            </span>
          </div>
        </div>
      </Card>
    );
  };

  // Loading state
  if (loadingModules) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="w-8 h-8" />
        <span className="ml-3 text-neutral-600">Loading schema...</span>
      </div>
    );
  }

  // Error state
  if (modulesError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-600">
        <AlertCircle className="w-8 h-8 mb-2" />
        <span>Failed to load schema</span>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => refetchModules()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-neutral-200">
        {/* System tabs (only show if multiple systems) */}
        {systemIds.length > 1 && (
          <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-neutral-100 bg-neutral-50">
            {systemNames.map((name, index) => (
              <button
                key={systemIds[index]}
                onClick={() => setActiveSystemIndex(index)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeSystemIndex === index
                    ? 'bg-white text-primary-700 border border-b-0 border-neutral-200'
                    : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-primary-600" />
            <div>
              <h3 className="font-semibold text-lg">{activeSystemName}</h3>
              <p className="text-sm text-neutral-500">
                {modules?.length || 0} modules, {modules?.reduce((acc, m) => acc + (m.tables?.length ?? 0), 0) || 0} tables
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={viewMode === 'browse' ? 'default' : 'outline'}>
              {selectedTableIds.size} selected
            </Badge>

            {/* Show different buttons based on whether tables already exist */}
            {existingTables.length > 0 && !isLoadingExistingTables ? (
              <>
                {/* View existing selection button */}
                <Button
                  variant={showExistingTablesView ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowExistingTablesView(true);
                    setViewMode('browse');
                  }}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Selection ({existingTables.length})
                </Button>
                {/* Re-generate button */}
                <Button
                  variant={viewMode === 'digital-twin' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowExistingTablesView(false);
                    setViewMode('digital-twin');
                    // Force re-fetch recommendations
                    setDtRecommendation(null);
                    setKgFirstRecommendation(null);
                    fetchDigitalTwinRecommendations();
                  }}
                  disabled={isLoadingDtRecommendation}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingDtRecommendation ? 'animate-spin' : ''}`} />
                  Re-generate
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant={viewMode === 'digital-twin' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setViewMode('digital-twin');
                    if (!dtRecommendation && !kgFirstRecommendation && !isLoadingDtRecommendation) {
                      fetchDigitalTwinRecommendations();
                    }
                  }}
                  disabled={isLoadingDtRecommendation}
                >
                  <Brain className="w-4 h-4 mr-1" />
                  {useKGFirstMode ? 'KG-First Analysis' : 'AI Digital Twin'}
                </Button>
              </>
            )}
            {/* Hide table-based buttons in KG-First mode since we work with concepts, not tables */}
            {!useKGFirstMode && (
              <>
                <Button
                  variant={viewMode === 'suggestions' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowExistingTablesView(false);
                    setViewMode('suggestions');
                    setShowSuggestionsPanel(true);
                  }}
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  AI Suggestions
                </Button>
                <Button
                  variant={viewMode === 'browse' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowExistingTablesView(false);
                    setViewMode('browse');
                  }}
                >
                  <Filter className="w-4 h-4 mr-1" />
                  Browse All
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="p-4 border-b border-neutral-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search modules and tables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <X className="w-4 h-4 text-neutral-400 hover:text-neutral-600" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Existing Tables View - shown when user clicks "View Selection" */}
        {showExistingTablesView && existingTables.length > 0 ? (
          <div className="p-4">
            {/* Summary Card */}
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 mb-6">
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Previously Selected Tables</h3>
                      <p className="text-sm text-neutral-600">
                        These tables were selected and saved to Neo4j from a previous session
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-lg px-3 py-1">
                    {existingTables.length} tables
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Group tables by system */}
            {(() => {
              const tablesBySystem = existingTables.reduce((acc, table) => {
                const systemName = table.system_name || 'Unknown System';
                if (!acc[systemName]) {
                  acc[systemName] = [];
                }
                acc[systemName].push(table);
                return acc;
              }, {} as Record<string, SelectedTableInfo[]>);

              return Object.entries(tablesBySystem).map(([systemName, tables]) => (
                <div key={systemName} className="mb-6">
                  <h4 className="flex items-center gap-2 font-semibold text-neutral-700 mb-3">
                    <Database className="w-4 h-4" />
                    {systemName}
                    <Badge variant="outline">{tables.length} tables</Badge>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {tables.map((table) => (
                      <Card
                        key={table.table_id}
                        className="p-3 border-green-200 bg-green-50/50"
                      >
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{table.table_name}</p>
                            {table.module_name && (
                              <p className="text-xs text-neutral-500 truncate">{table.module_name}</p>
                            )}
                            {table.column_count && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {table.column_count} columns
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ));
            })()}

            {/* Action buttons */}
            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={() => {
                  setShowExistingTablesView(false);
                  setViewMode('browse');
                }}
              >
                <Filter className="w-4 h-4 mr-2" />
                Browse & Modify
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowExistingTablesView(false);
                  setViewMode('digital-twin');
                  setDtRecommendation(null);
                  setKgFirstRecommendation(null);
                  fetchDigitalTwinRecommendations();
                }}
                disabled={isLoadingDtRecommendation}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingDtRecommendation ? 'animate-spin' : ''}`} />
                Re-generate with AI
              </Button>
            </div>
          </div>
        ) : viewMode === 'digital-twin' ? (
          // AI Digital Twin View
          <div className="p-4">
            {/* Loading State — skeleton that mirrors the loaded recommendation
                layout (summary card → KG-area chips → category tabs → 6 concept
                cards) so when data lands the page fills in instead of jumping.
                The status banner at the top keeps the "AI is analyzing… 1-2
                minutes" expectation that the previous spinner conveyed. */}
            {isLoadingDtRecommendation && (
              <div className="space-y-6">
                {/* Status banner — replaces the centered brain+spinner block.
                    Uses a soft purple band so it still reads as "AI is working"
                    without taking over the whole viewport. */}
                <div className="flex items-start gap-3 p-4 rounded-lg bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200">
                  <Brain className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5 animate-pulse" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-purple-900">
                      {useKGFirstMode ? 'Analyzing Strategic Questions' : 'Analyzing Enterprise Systems'}
                    </h3>
                    <p className="text-sm text-purple-800/80 mt-0.5">
                      {useKGFirstMode
                        ? 'AI is analyzing your strategic questions to identify required KG Areas and recommend conceptual entities for your knowledge graph.'
                        : `AI is analyzing your strategic questions and ${systemIds.length} enterprise system(s) to recommend the optimal tables for your digital twin.`
                      }
                    </p>
                    <p className="text-xs text-purple-700 mt-1.5 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      This may take 1-2 minutes…
                    </p>
                  </div>
                </div>

                {/* Summary card skeleton (mirrors the emerald summary panel) */}
                <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-emerald-200 animate-pulse flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-emerald-200/70 rounded animate-pulse w-64" />
                          <div className="h-3 bg-emerald-100 rounded animate-pulse w-48" />
                        </div>
                      </div>
                      <div className="h-6 w-32 bg-emerald-100 rounded-full animate-pulse flex-shrink-0" />
                    </div>
                    {/* 4-stat tile row skeleton */}
                    <div className="grid grid-cols-4 gap-4 mt-4">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="bg-white/60 rounded-lg p-3 text-center space-y-2">
                          <div className="h-6 bg-neutral-200 rounded animate-pulse w-10 mx-auto" />
                          <div className="h-3 bg-neutral-100 rounded animate-pulse w-16 mx-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                {/* KG Areas Identified skeleton — pill chips */}
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded bg-emerald-200 animate-pulse" />
                    <div className="h-4 bg-neutral-200 rounded animate-pulse w-40" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[80, 100, 60, 90, 110, 70].map((w, i) => (
                      <div
                        key={i}
                        className="h-6 bg-neutral-100 rounded-full animate-pulse"
                        style={{ width: `${w}px` }}
                      />
                    ))}
                  </div>
                </div>

                {/* Category tab strip skeleton */}
                <div className="flex items-center gap-2 border-b border-neutral-200 pb-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-9 w-32 bg-neutral-100 rounded-t-lg animate-pulse" />
                  ))}
                </div>

                {/* Concept cards grid skeleton — 6 placeholder cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="p-4 border-2 border-neutral-200 bg-white rounded-lg space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <div className="h-5 bg-neutral-200 rounded animate-pulse w-32" />
                          <div className="h-3 bg-neutral-100 rounded animate-pulse w-24" />
                        </div>
                        <div className="h-5 w-20 bg-neutral-100 rounded animate-pulse flex-shrink-0" />
                      </div>
                      <div className="space-y-1.5">
                        <div className="h-3 bg-neutral-100 rounded animate-pulse w-full" />
                        <div className="h-3 bg-neutral-100 rounded animate-pulse w-4/5" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error State */}
            {dtRecommendationError && !isLoadingDtRecommendation && (
              <div className="flex flex-col items-center justify-center py-12 text-red-600">
                <AlertCircle className="w-12 h-12 mb-3" />
                <h3 className="font-semibold">Failed to Generate Recommendations</h3>
                <p className="text-sm text-neutral-600 mt-1">{dtRecommendationError}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={fetchDigitalTwinRecommendations}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}

            {/* KG-First Concept Recommendations View */}
            {kgFirstRecommendation && !isLoadingDtRecommendation && useKGFirstMode && (
              <div className="space-y-6">
                {/* Summary Card */}
                <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Brain className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">KG-First Concept Recommendations</h3>
                          <p className="text-sm text-neutral-600">
                            {kgFirstRecommendation.summary.total_concepts} concepts from {kgFirstRecommendation.summary.kg_areas_count} KG Areas
                            {kgFirstRecommendation.llm_provider && ` • ${kgFirstRecommendation.llm_provider}`}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                        Concept-First Design
                      </Badge>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-4 gap-4 mt-4">
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-red-600">{kgFirstRecommendation.summary.must_have_count}</div>
                        <div className="text-xs text-neutral-600">Critical</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-amber-600">{kgFirstRecommendation.summary.should_have_count}</div>
                        <div className="text-xs text-neutral-600">Foundation</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-blue-600">{kgFirstRecommendation.summary.could_have_count}</div>
                        <div className="text-xs text-neutral-600">Enrichment</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-purple-600">{kgFirstRecommendation.strategic_questions.length}</div>
                        <div className="text-xs text-neutral-600">Questions</div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* KG Areas Identified */}
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Database className="w-5 h-5 text-emerald-600" />
                    KG Areas Identified
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {kgFirstRecommendation.kg_areas_identified.map((area) => (
                      <Badge
                        key={area.area_id}
                        variant="outline"
                        className={`${
                          area.relevance === 'high'
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : area.relevance === 'medium'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}
                      >
                        {area.area_name}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="flex items-center gap-2 border-b border-neutral-200 pb-2">
                  <button
                    onClick={() => setDtRecommendationCategory('must_have')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                      dtRecommendationCategory === 'must_have'
                        ? 'bg-red-100 text-red-800 border border-b-0 border-red-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <Star className="w-4 h-4" />
                    Critical ({kgFirstRecommendation.recommendations.must_have.concepts.length})
                  </button>
                  <button
                    onClick={() => setDtRecommendationCategory('should_have')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                      dtRecommendationCategory === 'should_have'
                        ? 'bg-amber-100 text-amber-800 border border-b-0 border-amber-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    Foundation ({kgFirstRecommendation.recommendations.should_have.concepts.length})
                  </button>
                  <button
                    onClick={() => setDtRecommendationCategory('could_have')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                      dtRecommendationCategory === 'could_have'
                        ? 'bg-blue-100 text-blue-800 border border-b-0 border-blue-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <Database className="w-4 h-4" />
                    Enrichment ({kgFirstRecommendation.recommendations.could_have.concepts.length})
                  </button>
                </div>

                {/* Concepts Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(dtRecommendationCategory === 'must_have'
                    ? kgFirstRecommendation.recommendations.must_have.concepts
                    : dtRecommendationCategory === 'should_have'
                    ? kgFirstRecommendation.recommendations.should_have.concepts
                    : kgFirstRecommendation.recommendations.could_have.concepts
                  ).map((concept) => (
                    <Card
                      key={concept.concept_id}
                      className={`p-4 border-2 transition-all ${
                        dtRecommendationCategory === 'must_have'
                          ? 'border-red-200 bg-red-50/50'
                          : dtRecommendationCategory === 'should_have'
                          ? 'border-amber-200 bg-amber-50/50'
                          : 'border-blue-200 bg-blue-50/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h5 className="font-semibold text-lg">{concept.concept_name}</h5>
                          <p className="text-xs text-neutral-500">{concept.kg_area_name}</p>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            concept.is_master_data
                              ? 'bg-purple-100 text-purple-700'
                              : concept.is_transactional
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {concept.is_master_data ? 'Master Data' : concept.is_transactional ? 'Transactional' : 'Analytical'}
                        </Badge>
                      </div>
                      <p className="text-sm text-neutral-600 mb-3">{concept.description}</p>

                      {/* Typical Properties */}
                      {(concept.typical_properties?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-neutral-500 mb-1">Properties:</p>
                          <div className="flex flex-wrap gap-1">
                            {concept.typical_properties!.slice(0, 4).map((prop) => (
                              <Badge key={prop.name} variant="secondary" className="text-xs">
                                {prop.name}
                              </Badge>
                            ))}
                            {concept.typical_properties!.length > 4 && (
                              <Badge variant="secondary" className="text-xs">
                                +{concept.typical_properties!.length - 4} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Key Relationships */}
                      {(concept.key_relationships?.length ?? 0) > 0 && (
                        <div>
                          <p className="text-xs font-medium text-neutral-500 mb-1">Relationships:</p>
                          <div className="flex flex-wrap gap-1">
                            {concept.key_relationships!.slice(0, 3).map((rel) => (
                              <Badge key={rel} variant="outline" className="text-xs bg-neutral-50">
                                {rel}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>

                {/* Strategic Questions Addressed */}
                {kgFirstRecommendation.strategic_questions.length > 0 && (
                  <div className="bg-white border border-neutral-200 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Info className="w-5 h-5 text-blue-600" />
                      Strategic Questions Driving This Design
                    </h4>
                    <div className="space-y-2">
                      {kgFirstRecommendation.strategic_questions.map((q) => (
                        <div key={q.question_id} className="text-sm text-neutral-700 flex items-start gap-2">
                          <span className="text-blue-500 mt-0.5">•</span>
                          {q.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Steps Info */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-sm text-emerald-800">
                    <strong>Note:</strong> These are conceptual entities based on your strategic questions and KG Areas taxonomy.
                    In Phase 3, these concepts will be used to generate a complete Knowledge Graph schema with semantic relationships.
                    Click <strong>Next</strong> to proceed to relationship discovery.
                  </p>
                </div>
              </div>
            )}

            {/* Table-based Recommendations View (fallback) */}
            {dtRecommendation && !isLoadingDtRecommendation && !useKGFirstMode && (
              <div className="space-y-6">
                {/* Summary Card */}
                <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Brain className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">AI Digital Twin Recommendations</h3>
                          <p className="text-sm text-neutral-600">
                            Generated in {dtRecommendation.processing_time_seconds.toFixed(1)}s using {dtRecommendation.llm_provider}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {Math.round(dtRecommendation.coverage_analysis.digital_twin_completeness * 100)}%
                          </div>
                          <div className="text-xs text-neutral-500">Coverage</div>
                        </div>
                        <Button variant="primary" size="sm" onClick={selectAllMustHave}>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Select All Must-Have
                        </Button>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-4 gap-4 mt-4">
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-red-600">{dtRecommendation.summary.total_must_have}</div>
                        <div className="text-xs text-neutral-600">Must-Have</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-amber-600">{dtRecommendation.summary.total_should_have}</div>
                        <div className="text-xs text-neutral-600">Should-Have</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-blue-600">{dtRecommendation.summary.total_could_have}</div>
                        <div className="text-xs text-neutral-600">Could-Have</div>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-purple-600">{dtRecommendation.summary.cross_system_joins}</div>
                        <div className="text-xs text-neutral-600">Cross-System Joins</div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Category Tabs */}
                <div className="flex items-center gap-2 border-b border-neutral-200 pb-2">
                  <button
                    onClick={() => setDtRecommendationCategory('must_have')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                      dtRecommendationCategory === 'must_have'
                        ? 'bg-red-100 text-red-800 border border-b-0 border-red-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <Star className="w-4 h-4" />
                    Must-Have ({dtRecommendation.must_have_tables.length})
                  </button>
                  <button
                    onClick={() => setDtRecommendationCategory('should_have')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                      dtRecommendationCategory === 'should_have'
                        ? 'bg-amber-100 text-amber-800 border border-b-0 border-amber-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    Should-Have ({dtRecommendation.should_have_tables.length})
                  </button>
                  <button
                    onClick={() => setDtRecommendationCategory('could_have')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
                      dtRecommendationCategory === 'could_have'
                        ? 'bg-blue-100 text-blue-800 border border-b-0 border-blue-200'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <Database className="w-4 h-4" />
                    Could-Have ({dtRecommendation.could_have_tables.length})
                  </button>
                  <div className="flex-1" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectAllInDtCategory(dtRecommendationCategory)}
                  >
                    Select All in Category
                  </Button>
                </div>

                {/* Tables Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(dtRecommendationCategory === 'must_have'
                    ? dtRecommendation.must_have_tables
                    : dtRecommendationCategory === 'should_have'
                    ? dtRecommendation.should_have_tables
                    : dtRecommendation.could_have_tables
                  ).map((table) => (
                    <RecommendedTableCard
                      key={table.table_id}
                      table={table}
                      isSelected={selectedTableIds.has(table.table_id)}
                      onToggle={() => toggleTableSelection(table.table_id)}
                      variant={dtRecommendationCategory === 'must_have' ? 'critical' : dtRecommendationCategory === 'should_have' ? 'high' : 'medium'}
                    />
                  ))}
                </div>

                {/* Cross-System Relationships */}
                {dtRecommendation.cross_system_relationships.length > 0 && (
                  <div className="mt-6">
                    <h4 className="flex items-center gap-2 font-semibold text-lg mb-3">
                      <GitBranch className="w-5 h-5 text-purple-600" />
                      Cross-System Relationships ({dtRecommendation.cross_system_relationships.length})
                    </h4>
                    <div className="space-y-2">
                      {dtRecommendation.cross_system_relationships.slice(0, 5).map((rel, idx) => (
                        <div key={rel.relationship_id || idx} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg text-sm">
                          <Badge variant="outline">{rel.source_table}</Badge>
                          <span className="text-neutral-400">{rel.source_column}</span>
                          <span className="text-purple-600 font-medium">→</span>
                          <Badge variant="outline">{rel.target_table}</Badge>
                          <span className="text-neutral-400">{rel.target_column}</span>
                          <Badge variant="secondary" className="ml-auto text-xs">
                            {Math.round(rel.confidence * 100)}% confidence
                          </Badge>
                        </div>
                      ))}
                      {dtRecommendation.cross_system_relationships.length > 5 && (
                        <p className="text-sm text-neutral-500 text-center py-2">
                          +{dtRecommendation.cross_system_relationships.length - 5} more relationships
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {dtRecommendation.coverage_analysis.recommendations.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="flex items-center gap-2 font-semibold mb-2">
                      <Info className="w-5 h-5 text-blue-600" />
                      AI Recommendations
                    </h4>
                    <ul className="space-y-1">
                      {dtRecommendation.coverage_analysis.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-sm text-neutral-700 flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Empty State - No recommendations yet (only show if no KG-First or legacy recommendations) */}
            {!kgFirstRecommendation && !dtRecommendation && !isLoadingDtRecommendation && !dtRecommendationError && (
              <div className="flex flex-col items-center justify-center py-16">
                <Brain className="w-16 h-16 text-neutral-300 mb-4" />
                <h3 className="text-lg font-semibold">KG-First Concept Analysis</h3>
                <p className="text-neutral-600 mt-2 text-center max-w-md">
                  Click <strong>Re-generate</strong> above to analyze your strategic questions and get AI-powered
                  concept recommendations based on the KG Areas taxonomy.
                </p>
              </div>
            )}
          </div>
        ) : viewMode === 'suggestions' ? (
          // AI Suggestions View
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-secondary-500" />
                <h4 className="font-medium">AI-Recommended Tables</h4>
                <Info className="w-4 h-4 text-neutral-400" />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchSuggestions()}
                  disabled={loadingSuggestions}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingSuggestions ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={selectAllSuggestions}>
                  Select All
                </Button>
              </div>
            </div>

            {loadingSuggestions ? (
              <div className="flex items-center justify-center py-12">
                <Spinner className="w-6 h-6" />
                <span className="ml-2 text-neutral-600">Analyzing questions...</span>
              </div>
            ) : suggestionsData?.suggestions && suggestionsData.suggestions.length > 0 ? (
              <div className="grid gap-3">
                {suggestionsData.suggestions.map(renderSuggestion)}
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <Table2 className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p>No suggestions available</p>
                <p className="text-sm mt-1">Try adding more strategic questions</p>
              </div>
            )}
          </div>
        ) : (
          // Browse View
          <div>
            {filteredModules.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                <Database className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p>No modules found</p>
              </div>
            ) : (
              filteredModules.map((module) => (
                <div key={module.module_id} className="border-b border-neutral-200">
                  {/* Module header */}
                  <div
                    className="flex items-center gap-3 px-4 py-3 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                    onClick={() => toggleModule(module.module_id)}
                  >
                    <button className="p-0.5">
                      {expandedModules.has(module.module_id) ? (
                        <ChevronDown className="w-5 h-5 text-neutral-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-neutral-500" />
                      )}
                    </button>
                    <Columns3 className="w-5 h-5 text-primary-600" />
                    <div className="flex-1">
                      <span className="font-medium">{module.module_name}</span>
                      {module.description && (
                        <p className="text-sm text-neutral-500">{module.description}</p>
                      )}
                    </div>
                    <Badge variant="outline">{module.table_count} tables</Badge>
                  </div>

                  {/* Module tables */}
                  {expandedModules.has(module.module_id) && (
                    <div className="bg-white">
                      {(module.tables ?? []).map((table) => renderTable(table, module.module_name))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer with actions */}
      {/* In KG-First mode with recommendations, show concept count instead of tables */}
      {kgFirstRecommendation && viewMode === 'digital-twin' ? (
        <div className="p-4 border-t border-neutral-200 bg-emerald-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-emerald-800">
                {kgFirstRecommendation.summary.total_concepts} concepts identified from {kgFirstRecommendation.summary.kg_areas_count} KG Areas
              </span>
            </div>
            <div className="text-sm text-emerald-600">
              Click <strong>Next</strong> to proceed with KG-First schema generation
            </div>
          </div>
        </div>
      ) : selectedTableIds.size > 0 ? (
        <div className="p-4 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-primary-600" />
              <span className="font-medium">{selectedTableIds.size} tables selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedTableIds(new Set())}
              >
                Clear Selection
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveSelection}
                loading={selectTablesMutation.isPending}
              >
                Save Selection
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SchemaExplorationPanel;
